export type PrototypeHealthTone = 'HEALTHY' | 'ATTENTION' | 'CRITICAL';

export type PrototypeDataKind = 'MOCK' | 'LIVE' | 'PLACEHOLDER';

export interface PrototypeScoreCard {
  label: string;
  score: number | null;
  tone: PrototypeHealthTone;
  dataKind: PrototypeDataKind;
  supportingText: string;
  statusLabel?: string;
}

export interface PrototypeHealthDomain extends PrototypeScoreCard {
  overallParticipation: 'ACTIVE' | 'PLANNED' | 'UNSCORED';
}

export interface PrototypeDestinationSummary {
  destination: 'LOCAL' | 'CLOUD';
  currentCount: number;
  observedCount: number;
  tone: PrototypeHealthTone;
}

export interface PrototypeClientCard {
  conditionNotes?: readonly { deviceName: string; reason: string }[];
  diagnostics?: readonly { deviceName: string; messages: readonly string[] }[];
  organizationId: number;
  clientName: string;
  backupScore: number | null;
  tone: PrototypeHealthTone;
  monitoredDeviceCount: number;
  healthyCount: number;
  degradedCount: number;
  failedCount: number;
  local: PrototypeDestinationSummary;
  cloud: PrototypeDestinationSummary;
}

export interface PrototypeIssue {
  issueId: string;
  severity: 'FAILED' | 'ATTENTION';
  clientName: string;
  deviceName: string;
  summary: string;
  diagnostics?: readonly string[];
  ninjaDeviceUrl: string | null;
}

export interface MspHealthPrototypeModel {
  generatedAtLabel: string;
  managedClientCount: number;
  overall: PrototypeScoreCard;
  categories: readonly PrototypeHealthDomain[];
  clients: readonly PrototypeClientCard[];
  issues: readonly PrototypeIssue[];
}

export function deriveOverallHealth(
  domains: readonly PrototypeHealthDomain[],
): PrototypeScoreCard {
  const activeDomains = domains.filter((domain) => domain.overallParticipation === 'ACTIVE');
  if (activeDomains.length === 0) {
    throw new Error('Overall MSP Health requires at least one active health domain');
  }

  const scores = activeDomains.flatMap((domain) => domain.score === null ? [] : [domain.score]);
  const score = scores.length === 0
    ? null
    : Math.round(scores.reduce((total, value) => total + value, 0) / scores.length);
  const tone = activeDomains.reduce<PrototypeHealthTone>(
    (worst, domain) => worseTone(worst, domain.tone),
    'HEALTHY',
  );
  return {
    label: 'Overall MSP Health',
    score,
    tone,
    dataKind: activeDomains.every((domain) => domain.dataKind === 'LIVE') ? 'LIVE' : 'MOCK',
    supportingText: `${activeDomains.length} active health ${activeDomains.length === 1 ? 'domain' : 'domains'}`,
  };
}

function worseTone(left: PrototypeHealthTone, right: PrototypeHealthTone): PrototypeHealthTone {
  const severity: Record<PrototypeHealthTone, number> = {
    HEALTHY: 0,
    ATTENTION: 1,
    CRITICAL: 2,
  };
  return severity[right] > severity[left] ? right : left;
}
